import { Divider } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Chip from "@mui/material/Chip";
import { FilterChangedEvent } from "ag-grid-community";
import GridEventProvider from "components/context/GridEventContext";
import { useAxios } from "myAxios";
import dynamic from "next/dynamic";
import { FC, useContext, useEffect, useState } from "react";
import { LoadingStatus } from "types/app";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  wrapper: {
    padding: "10px",
    width: "100%",
    flex: 1,
  },
  title: {
    color: "rgba(0, 0, 0, 0.54)",
    fontWeight: "bold",
    display: "flex",
    justifyContent: "center",
    fontSize: 16,
  },
  noneSavedSearch: {
    color: "rgba(0, 0, 0, 0.54)",
    fontWeight: 600,
    flex: "auto",
    flexDirection: "column",
    flexWrap: "nowrap",
    display: "flex",
    flexFlow: "column nowrap",
  },
  helpText: {
    paddingTop: "10px",
  },
}));

export interface LabelData {
  data: string;
  name: string;
  id: string;
}

const SaveSearchPanel: FC<{ pageId: string }> = ({ pageId }) => {
  const classes = useStyles();
  const axios = useAxios();
  const { gridEvent } = useContext(GridEventProvider);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("notLoaded");
  const [labelList, setLabelList] = useState<LabelData[]>([]);
  const [labelId, setLabelId] = useState("");
  const [advancedList, setAdvancedList] = useState<LabelData[]>([]);
  const [advancedId, setAdvancedId] = useState("");

  const initPanel = () => {
    Promise.all([
      axios.get(`/api/portfolio/savedSearch?type=${pageId}`),
      axios.get(`/api/portfolio/savedSearch?type=${pageId}-advanced`),
    ])
      .then(([res1, res2]) => {
        setLoadingStatus("loaded");
        setLabelList(res1.data);
        setAdvancedList(res2.data);
      })
      .catch(() => {
        setLoadingStatus("failed");
      });
  };

  // load label
  useEffect(() => {
    addEventListener("SAVE_SEARCH", (e) => {
      const { labelData } = (e as any).detail;
      if (loadingStatus === "notLoaded") {
        initPanel();
      } else {
        setLabelList([{ ...labelData }, ...labelList]);
      }
    });
  }, []);

  useEffect(() => {
    window.addEventListener("SAVE_SEARCH_ADVANCED", (e) => {
      const { labelData } = (e as any).detail;
      if (loadingStatus === "notLoaded") {
        initPanel();
      } else {
        setAdvancedList([{ ...labelData }, ...advancedList]);
        setAdvancedId(labelData.id);
      }
    });
  }, []);

  useEffect(() => {
    initPanel();
  }, []);

  useEffect(() => {
    if (gridEvent) {
      gridEvent.api.addEventListener("filterChanged", (e: FilterChangedEvent) => {
        const filterModel = e.api.getFilterModel();
        if (!("externalAdvancedFilters" in filterModel)) {
          setAdvancedId("");
        }
      });
    }
  }, [gridEvent]);

  const handleClick = (item: LabelData) => () => {
    labelList.filter((i) => {
      if (i.id === item.id) {
        setLabelId(item.id);
      }
    });
    gridEvent?.api.setFilterModel(JSON.parse(item.data));
    // if (!!item.sortModel) {
    //   gridEvent?.api.setSortModel(item.sortModel);
    // }
  };

  const handleAdvancedClick = (item: LabelData) => () => {
    setAdvancedId(item.id);
    window.dispatchEvent(
      new CustomEvent<{ id: string; name: string; data: string }>("RUN_ADVANCED_QUERY", {
        detail: {
          id: item.id,
          name: item.name,
          data: item.data,
        },
      })
    );
  };

  const handleDelete = (item: LabelData) => () => {
    axios
      .delete(`/api/portfolio/savedSearch/${item.id}`)
      .then(() => {
        setLabelList((prv) => prv.filter((i) => i.id !== item.id));
      })
      .catch(() => {
        // console.log(err);
      });
  };

  const handleAdvancedDelete = (item: LabelData) => () => {
    axios
      .delete(`/api/portfolio/savedSearch/${item.id}`)
      .then(() => {
        setAdvancedList((prv) => prv.filter((i) => i.id !== item.id));
      })
      .catch(() => {
        // console.log(err);
      });
  };

  return (
    <>
      <div className={classes.wrapper}>
        <h4 className={classes.title}>Saved Searches</h4>
        {loadingStatus === "loaded" && labelList.length === 0 && advancedList.length === 0 && (
          <div className={classes.noneSavedSearch}>
            In order to use the Saved Searches panel, please follow the steps:
            <ol className={classes.helpText}>
              <li> Apply your search criteria</li>
              <li>Click on the "SAVE SEARCH" button</li>
              <li>
                In the modal dialogue, please double check your search criteria and add explanatory
                name to your search
              </li>
              <li>
                After you click on the "SAVE" button, the search will be available in this panel
              </li>
              <li>
                You can load your saved search criteria by clicking on the respective saved search
                item
              </li>
            </ol>
          </div>
        )}
        {loadingStatus === "loaded" && labelList.length > 0 && (
          <div className={classes.root}>
            {labelList.map((item: LabelData) => (
              <Chip
                key={item.id}
                label={item.name}
                clickable={true}
                onClick={handleClick(item)}
                onDelete={handleDelete(item)}
                color={labelId === item.id ? "primary" : "default"}
              />
            ))}
          </div>
        )}

        {loadingStatus === "loaded" && advancedList.length > 0 && (
          <>
            <Divider />
            <div className={classes.root}>
              {advancedList.map((item: LabelData) => (
                <Chip
                  key={item.id}
                  label={item.name}
                  clickable={true}
                  onClick={handleAdvancedClick(item)}
                  onDelete={handleAdvancedDelete(item)}
                  color={advancedId === item.id ? "primary" : "default"}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default dynamic(() => Promise.resolve(SaveSearchPanel), { ssr: false });
